import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { KeyRound, Menu, X, Briefcase } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const LINKS = [
  { label: "Chi Siamo", href: "#chi-siamo" },
  { label: "Aree di Attività", href: "#aree-attivita" },
  { label: "Sedi", href: "#sedi" },
  { label: "Contatti", href: "#contatti" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (onHome) scrollToId(href);
    else navigate(`/${href}`);
  };

  const goHome = () => {
    setOpen(false);
    if (onHome) scrollToTop();
    else navigate("/");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "bg-navy/95 backdrop-blur-md border-b border-gold/20 shadow-lg shadow-black/20" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <button onClick={goHome} className="flex items-center gap-3 text-left" data-testid="nav-logo">
          <img src="/brand/emblem.png" alt="Logo Studio Legale Napolitano" className="h-14 w-14 object-contain" data-testid="nav-logo-image" />
          <span className="leading-tight">
            <span className="block font-serif text-white text-base sm:text-lg">Giuseppe Napolitano</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">Avvocato</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-medium text-slate-300 hover:text-gold transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/lavora-con-noi"
            data-testid="nav-link-lavora-con-noi"
            className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${location.pathname === "/lavora-con-noi" ? "text-gold" : "text-slate-300 hover:text-gold"}`}
          >
            <Briefcase className="h-3.5 w-3.5" /> Lavora con noi
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("#contatti")}
            data-testid="nav-contact-button"
            className="hidden sm:inline-flex items-center bg-gold px-5 py-2.5 text-sm font-semibold text-navy hover:bg-gold-light transition-colors"
          >
            Richiedi Consulenza
          </button>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            title="Area Riservata"
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 hover:border-gold/50 hover:text-gold transition-colors"
          >
            <KeyRound className="h-4 w-4" />
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden flex h-10 w-10 items-center justify-center border border-white/15 text-white"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-white/10 bg-navy px-6 py-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="py-3 text-left text-sm font-medium text-slate-200 hover:text-gold transition-colors border-b border-white/5 last:border-0"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/lavora-con-noi"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-lavora-con-noi"
            className="py-3 text-left text-sm font-medium text-gold hover:text-gold-light transition-colors border-b border-white/5"
          >
            Lavora con noi
          </Link>
          <button
            onClick={() => go("#contatti")}
            className="mt-3 bg-gold px-5 py-3 text-sm font-semibold text-navy hover:bg-gold-light transition-colors"
          >
            Richiedi Consulenza
          </button>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
